import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import { subscribeSocial, toggleLike, registerView } from '../firebase/socialService';
import './Social.css';

// Compact counts: 1200 -> 1.2K. A four-digit number in a small pill pushes the
// row wide on a phone, and nobody reads the exact figure anyway.
function short(n) {
  const v = Number(n) || 0;
  if (v < 1000) return String(v);
  if (v < 1000000) return `${(v / 1000).toFixed(v < 10000 ? 1 : 0)}K`.replace('.0', '');
  return `${(v / 1000000).toFixed(1)}M`.replace('.0', '');
}

/* ══════════════════════════════════════════════════════════════════════════
   STATIC STATS — feeds and grids
   ══════════════════════════════════════════════════════════════════════════
   Numbers only, no listener, no interaction. Takes whatever the caller already
   fetched in ONE batched query for the whole grid (fetchStats), so a tile
   costs nothing of its own. */
export function SocialStats({ stats, className = '' }) {
  const { t } = useT();
  if (!stats) return null;
  const { likesCount = 0, viewsCount = 0, commentsCount = 0 } = stats;
  if (!likesCount && !viewsCount && !commentsCount) return null;

  return (
    <span className={`ps-stats ${className}`.trim()}>
      <span className="ps-stat" title={t('ps_views')}>
        <EyeIcon /> {short(viewsCount)}
      </span>
      <span className="ps-stat" title={t('ps_likes')}>
        <HeartIcon /> {short(likesCount)}
      </span>
      {/* Costs no extra read: `commentsCount` is a field on the same document
          the grid already fetched in one batched query, kept in step by a
          Cloud Function (functions/onCommentWrite.js). */}
      <span className="ps-stat" title={t('cm_title')}>
        <CommentIcon /> {short(commentsCount)}
      </span>
    </span>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   LIVE BAR — an open item
   ══════════════════════════════════════════════════════════════════════════
   Like button and view count on a real-time listener. This is the ONE place a
   listener is worth its cost: you watch your own like land, and a count that
   moves while you are looking at it is the point.

   Mounting this also registers the view (once per session, see the service). */
// `onLikeChange(id, delta)` lets whatever rendered this keep its OWN copy of
// the numbers honest — a grid behind the lightbox holds statically fetched
// counts that would otherwise still show the pre-like figure after you close it.
export function SocialBar({
  kind, id, ownerUid, compact = false,
  commentsCount = null, commentsOpen = false, onComments,
  onLikeChange,
}) {
  const { user } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();

  const [social, setSocial] = useState(null);
  const [busy, setBusy] = useState(false);
  // Set while a like round-trip is in flight, so the listener echoing the old
  // value back doesn't make the heart flicker.
  const pending = useRef(null);

  useEffect(() => {
    if (!id) return undefined;
    registerView(kind, id);
    return subscribeSocial(kind, id, (s) => {
      if (pending.current !== null && s && (s.likedBy.includes(user?.uid) === pending.current)) {
        pending.current = null;               // server agrees; stop overriding
      }
      setSocial(s);
    });
  }, [kind, id, user?.uid]);

  const serverLiked = !!(user?.uid && social?.likedBy?.includes(user.uid));
  const liked = pending.current !== null ? pending.current : serverLiked;

  // You cannot like your own work. It was possible until now — the count was
  // real but meaningless, and it is plainly visible from your own dashboard.
  // The count still SHOWS; only the pressing is off, so the owner can read how
  // their piece is doing. Also enforced in firestore.rules, since a disabled
  // button is a courtesy and not a control.
  const isOwn = !!user?.uid && !!ownerUid && user.uid === ownerUid;

  async function onLike() {
    if (!user?.uid) { navigate('/login'); return; }
    if (busy || isOwn) return;
    const next = !liked;
    pending.current = next;                    // optimistic
    setBusy(true);
    try {
      await toggleLike(kind, id, user.uid, !next);
      onLikeChange?.(id, next ? 1 : -1);        // only once the server agreed
    } catch {
      pending.current = null;                  // rejected — fall back to server
    } finally {
      setBusy(false);
    }
  }

  if (!social) return null;

  // The optimistic count has to move with the optimistic heart, or the button
  // says "Liked" next to a number that hasn't changed.
  const likes = social.likesCount + (pending.current === null ? 0 : (liked === serverLiked ? 0 : (liked ? 1 : -1)));

  // Named explicitly rather than relying on the visible label: in the lightbox
  // the word is hidden and the button is the heart alone (see .glb-meta), which
  // would otherwise leave it with no accessible name at all.
  const likeLabel = isOwn ? t('ps_like_own')
    : user ? (liked ? t('ps_unlike') : t('ps_like'))
      : t('ps_login_to_like');

  return (
    <div className={`ps-bar${compact ? ' ps-bar--compact' : ''}`}>
      <button
        type="button"
        className={`ps-like${liked ? ' ps-like--on' : ''}${isOwn ? ' ps-like--own' : ''}`}
        onClick={onLike}
        disabled={busy || isOwn}
        aria-pressed={liked}
        aria-label={likeLabel}
        title={likeLabel}
      >
        <HeartIcon filled={liked} />
        <span className="ps-like-label">{liked ? t('ps_liked') : t('ps_like')}</span>
        <span className="ps-like-n">{short(Math.max(0, likes))}</span>
      </button>

      <span className="ps-pill" title={t('ps_views')}>
        <EyeIcon /> {short(social.viewsCount)}
      </span>

      {/* THE COUNT IS THE CONTROL. Comments start closed on a narrow screen,
          so something has to open them, and a separate "show comments" button
          beside a comment count is two things saying one thing. */}
      {commentsCount !== null && (onComments ? (
        <button
          type="button"
          className={`ps-pill ps-pill--btn${commentsOpen ? ' ps-pill--on' : ''}`}
          onClick={onComments}
          aria-expanded={commentsOpen}
          aria-label={t('cm_title')}
          title={t('cm_title')}
        >
          <CommentIcon /> {short(commentsCount)}
        </button>
      ) : (
        <span className="ps-pill" title={t('cm_title')}>
          <CommentIcon /> {short(commentsCount)}
        </span>
      ))}
    </div>
  );
}

/* ── icons ─────────────────────────────────────────────────────────────── */
function HeartIcon({ filled = false }) {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1.1L12 21.2l7.8-7.7 1-1.1a5.5 5.5 0 0 0 0-7.8z" />
    </svg>
  );
}
function CommentIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9 9 0 0 1-3.8-.8L3 21l1.9-5.1A8.4 8.4 0 0 1 12 3.1a8.4 8.4 0 0 1 9 8.4z" />
    </svg>
  );
}
function EyeIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
  );
}
